import React, { useMemo } from 'react'
import { useSignUpData, useGetUserByToken, UserRole } from '@modmd/data'
import { UserDataForm } from 'internal-portal/routes/components/UserDataForm'
import { COLOR, DEVICE, pxToRem, fontSizes } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import TextInput from 'components/TextInput'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import Button from 'components/Button'
import Card from 'components/Card'
import Logo from 'components/Logo'
import { TermsAndConditions } from 'components/TermsAndConditions'
import { Checkbox } from 'components/Checkbox'
import { ConsertTermsText } from 'utils/helpers'

interface Props {
  token: string
  title: string
  passwordLabel: string
  passwordPlaceholder: string
  passwordConfirmationLabel: string
  passwordConfirmationPlaceholder: string
  signUpButtonLabel: string
  alreadyHaveAccountLabel: string
  signInButtonLabel: string
  onSignInButtonOnClick: () => void
  onSignUpSuccess?: () => void
  onSignUpFailure?: () => void
}

export const SignUpCard: React.VFC<Props> = ({
  token,
  title,
  passwordLabel,
  passwordPlaceholder,
  passwordConfirmationLabel,
  passwordConfirmationPlaceholder,
  signUpButtonLabel,
  alreadyHaveAccountLabel,
  onSignInButtonOnClick,
  signInButtonLabel,
  onSignUpSuccess,
  onSignUpFailure,
}) => {
  const [isConsentTerm, setIsConsentTerm] = React.useState(false)
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { data } = useGetUserByToken({
    token,
  })

  const isStaff = useMemo(() => {
    const adminRoles = [
      UserRole.ADMIN,
      UserRole.CLS,
      UserRole.LIAISON,
      UserRole.NURSE,
      UserRole.RESULTS,
      UserRole.SCHEDULER,
      UserRole.SUPER_ADMIN,
    ]
    return adminRoles.includes(data?.userByToken?.UserRoles[0].role as UserRole)
  }, [data])

  const validationSchema = React.useMemo(() => (isStaff ? 'passwordValidators' : 'noValidators'), [
    isStaff,
  ])

  const { signUpForm, userForm, isUserByTokenDataLoading, isSignUpLoading } = useSignUpData({
    token,
    onCompleted: onSignUpSuccess,
    onError: onSignUpFailure,
    validationSchema,
  })

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = signUpForm

  return (
    <Card width="100%" maxWidth={pxToRem(500)}>
      <Card.Title rightContent={<Logo isSmall />}>{title}</Card.Title>
      <Card.Content>
        <form onSubmit={handleSubmit}>
          <Box display="grid" gridGap={pxToRem(15)} mt={isMobile ? pxToRem(150) : undefined}>
            <Box display="inline-grid" justifySelf="center" gridAutoFlow="column" gridGap="0.25rem">
              <Text>{alreadyHaveAccountLabel}</Text>
              <Button onClick={onSignInButtonOnClick} appearance="link">
                <Text color={COLOR.brand}>{signInButtonLabel}</Text>
              </Button>
            </Box>
            <TextInput
              name="password"
              type="password"
              label={passwordLabel}
              placeholder={passwordPlaceholder}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              errorMessage={errors.password && touched.password ? errors.password : undefined}
              autoComplete="off"
            />
            <TextInput
              name="passwordConfirmation"
              type="password"
              label={passwordConfirmationLabel}
              placeholder={passwordConfirmationPlaceholder}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.passwordConfirmation}
              errorMessage={
                errors.passwordConfirmation && touched.passwordConfirmation
                  ? errors.passwordConfirmation
                  : undefined
              }
              autoComplete="off"
            />
            <Text fontSize={fontSizes.l}>User Information</Text>
            <TextInput value={userForm.values.email || ''} label="Email" disabled isFullWidth />
            {isUserByTokenDataLoading ? (
              <Box mb="4rem">
                <Text color={COLOR.brand}>Loading data, please wait a second ...</Text>
              </Box>
            ) : (
              <>
                <UserDataForm userForm={userForm} isSignUp />
                <Box m="1rem 0">
                  <Text color={COLOR.danger} fontSize={fontSizes.m}>
                    I have read and agree to:
                  </Text>
                  <TermsAndConditions
                    isChecked={values.isHIPAAConfirmed}
                    onChange={() => {
                      void setFieldValue('isHIPAAConfirmed', !values.isHIPAAConfirmed)
                    }}
                    url={process.env.REACT_APP_HIPAA_RELEASE_PDF}
                    urlLabel="HIPAA Consent and Release"
                    errorMessage={
                      errors.isHIPAAConfirmed && touched.isHIPAAConfirmed
                        ? errors.isHIPAAConfirmed
                        : undefined
                    }
                  />
                  <Box mt=".5rem">
                    <TermsAndConditions
                      isChecked={values.isTOSConfirmed}
                      onChange={() => {
                        void setFieldValue('isTOSConfirmed', !values.isTOSConfirmed)
                      }}
                      url={process.env.REACT_APP_MODMDLA_TERMS}
                      urlLabel="modMD Terms of service"
                      errorMessage={
                        errors.isTOSConfirmed && touched.isTOSConfirmed
                          ? errors.isTOSConfirmed
                          : undefined
                      }
                    />
                  </Box>
                  <Checkbox
                    checked={isConsentTerm}
                    onChange={() => setIsConsentTerm((prev) => !prev)}
                    label={ConsertTermsText}
                  />
                </Box>
              </>
            )}
            <Button
              type="submit"
              disabled={isSignUpLoading || !signUpForm.isValid || !isConsentTerm}
            >
              {signUpButtonLabel}
            </Button>

            <Box marginY="1rem" textAlign="center">
              <Button
                size="small"
                colorVariant="secondary"
                appearance="link"
                href={process.env.REACT_APP_PRIVACY_AND_POLICY}
                target="_blank"
              >
                <Text fontSize={fontSizes.xs}>{process.env.REACT_APP_PRIVACY_AND_POLICY}</Text>
              </Button>
            </Box>
          </Box>
        </form>
      </Card.Content>
    </Card>
  )
}
